import type { Ref } from "vue"
import { ref } from "vue"

class ModalRegistry {
  public modals: Map<string | symbol, Ref<boolean>>

  constructor() {
    this.modals = new Map()
  }

  public register({ name, active }: { name: string | symbol, active?: Ref<boolean> }) {
    this.modals.set(name, active ?? ref(false))
  }

  public deregister(name: string | symbol) {
    this.modals.delete(name)
  }

  /**
   * Clears all modals from the internal collection.
   *
   * @warning
   * Use with caution: calling this method will remove all modals, which may have unintended side effects
   * if other parts of the application depend on the current modal state.
   */
  public clear() {
    this.modals.clear()
  }
}

export const registry = new ModalRegistry()

export function useModal(name: string | symbol) {
  if (!registry.modals.has(name)) {
    registry.register({ name })
  }

  const toggle = () => {
    const modal = registry.modals.get(name)

    if (!modal) {
      throw new Error(`Attempted to toggle modal that was not found in the registry: ${name.toString()}`)
    }

    modal.value = !modal.value
  }

  return {
    isActive: registry.modals.get(name) ?? ref(false),
    toggle,
    modals: registry.modals,
  }
}
